import { Download } from '@carbon/icons-react';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router'
import { useMemo } from 'react';
import { z } from 'zod';

import { campaignDetailQueryOptions } from '@/api/campaignDetail';
import { listCampaignsQueryOptions } from '@/api/listCampaigns';
import { Breadcrumbs } from '@/components/breadcrumbs';
import { AccordionSection } from '@/components/input/accordionSection';
import { Avatar } from '@/components/input/avatar';
import { Button } from '@/components/input/button';
import { Select } from '@/components/input/select';
import { useReportByCategory } from '@/hooks/useReportByCategory';
import { CampaignIdSchema } from '@/schemas/campaignId';
import { EmployeeIdSchema } from '@/schemas/employeeId';
import { memberReportOptions } from '@/util/campaignReport';

export const Route = createFileRoute('/dashboard/_authenticated/admin/reports/$campaignId/$employeeId')({
  component: RouteComponent,
  params: {
    parse: (parameters) => z.object({
      campaignId: z.preprocess(Number, CampaignIdSchema),
      employeeId: EmployeeIdSchema,
    }).parse(parameters)
  },
  errorComponent: ({ error }) => (
    <div className="text-body2 text-error-300">Invalid route params: {error.message}</div>
  ),
  // No breadcrumb of its own: the `reports` layout route already adds "Reports".
})

/**
 * One member's report for one campaign (specs/008-campaign-reports-page, contracts/member-report.md):
 * the selector switches between the campaigns the member was evaluated in, labelled by year.
 */
function RouteComponent() {
  const match = Route.useMatch();
  const navigate = Route.useNavigate();
  const { campaignId, employeeId } = Route.useParams();

  // Preferred data source per the reuse-vs-new decision: reuse
  // `campaignDetailQueryOptions` (`GET api/admin/campaigns/:id`) — it
  // already returns the full `Campaign` with `evaluationList` for every
  // employee, so a per-employee admin report is a client-side filter on
  // `evaluatee.uuid`, no new endpoint needed.
  const { data: campaign, isLoading } = useQuery(campaignDetailQueryOptions(campaignId));
  const { data: campaignsPage } = useQuery(listCampaignsQueryOptions());

  const employeeEvaluations = useMemo(
    () => campaign?.evaluationList.filter((evaluation) => evaluation.evaluatee.uuid === employeeId) ?? [],
    [campaign, employeeId],
  );

  const employee = employeeEvaluations[0]?.evaluatee;

  const scopedCampaign = useMemo(() => {
    if (!campaign) {
      return;
    }
    return { ...campaign, evaluationList: employeeEvaluations };
  }, [campaign, employeeEvaluations]);

  const report = useReportByCategory(scopedCampaign);

  const options = useMemo(() => {
    const fromList = memberReportOptions(campaignsPage?.content ?? [], employeeId);
    // The list may not have loaded (or may lag behind); the open campaign is always an option.
    if (campaign && !fromList.some((option) => option.campaignId === campaignId)) {
      return [...memberReportOptions([campaign], employeeId), ...fromList];
    }
    return fromList;
  }, [campaignsPage?.content, campaign, campaignId, employeeId]);

  if (isLoading) {
    return <div className="text-body2 text-neutral-600">Loading...</div>;
  }

  if (!campaign || employeeEvaluations.length === 0 || !employee) {
    return (
      <div className="flex flex-col items-start gap-3">
        <div className="text-body2 text-error-300">Report not found for this employee.</div>
        <Link className="
          text-body2 font-semibold text-primary-default
          hover:underline
        " to="/dashboard/admin/reports">
          Back to Reports
        </Link>
      </div>
    );
  }

  const label = options.find((option) => option.campaignId === campaignId)?.label ?? campaign.name;
  const hasReport = !!report && report.length > 0;

  return (
    <>
      <div className="
        pt-6
        print:hidden
      ">
        <Breadcrumbs crumbsToAppend={[
          { label: employee.name, path: match.pathname },
          { label, path: match.pathname },
        ]} />
      </div>
      <div className="flex flex-col gap-10 pb-12">
        <div className="
          flex flex-col gap-4 border-b-2 border-b-primary-300 pb-6
          sm:flex-row sm:items-center sm:justify-between
        ">
          <div className="flex min-w-0 items-center gap-4">
            <Avatar name={employee.name} pictureBase64={employee.picture} size="lg" />
            <div className="min-w-0">
              <h1 className="truncate text-page-title">{employee.name}</h1>
              {employee.role && <div className="text-body3 text-neutral-600">{employee.role}</div>}
            </div>
          </div>
          <div className="
            flex items-center gap-3
            print:hidden
          ">
            <Select
              className="max-w-60 min-w-28"
              hideLabel
              label="Report"
              onValueChange={(value) => {
                void navigate({
                  to: '/dashboard/admin/reports/$campaignId/$employeeId',
                  params: { campaignId: CampaignIdSchema.parse(Number(value)), employeeId },
                });
              }}
              options={options.map((option) => ({ value: String(option.campaignId), label: option.label }))}
              value={String(campaignId)}
            />
            <Button
              className="
                min-h-11 shrink-0
                lg:min-h-10
              "
              disabled={!hasReport}
              intent="primary"
              onClick={() => globalThis.print()}
              size="sm"
              type="button"
            >
              <Download aria-hidden size="16" />
              Download pdf
            </Button>
          </div>
        </div>
        <div className="flex flex-col gap-6">
          <h2 className="text-h3 text-primary-default">{`${label} Report`}</h2>
          {hasReport ? (
            <AccordionSection
              forceOpenAllOnPrint
              items={report.map((categoryLine) => ({
                key: categoryLine.category,
                title: categoryLine.category,
                content: categoryLine.evaluations.map(({ evaluatorId, form }) => (
                  <div className="measure" key={evaluatorId}>{form.comment}</div>
                )),
              }))}
            />
          ) : (
            <div className="text-body2 text-neutral-600">
              No report available yet for this campaign.
            </div>
          )}
        </div>
      </div>
    </>
  );
}
